import { type MutableRefObject, useCallback } from 'react'
import { toast } from 'sonner'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { resolveShapePastePlacement } from '@/lib/editor/clipboard-paste'
import { isGroupShape } from '@/lib/groups/group-kind'
import {
  remapClipboardParentGroupId,
  shapeSnapshotNestingDepth,
} from '@/lib/groups/clipboard-group-policy'
import { computeViewportCenteredPasteOffset } from '../helpers/paste-placement'
import type { CenterCanvasConfig } from '../types/center-canvas-config'
import type { ClipboardEntry } from '../types/clipboard-entry'

const LOCKED_PASTE_MESSAGE = 'Object Locked: Can’t paste locked objects'

/**
 * Paste (viewport-centered, in place, at a point, or offset for Duplicate) from the newest usable
 * clipboard entry, as one undo step that restores the copied objects' relative stacking order.
 */
export function useClipboardPaste({
  internalShapeManagerRef,
  scrollRef,
  getCanvasOrigin,
  scale,
  clipboardHistoryRef,
  canUseClipboardEntry,
  onShapeSelectionHistory,
  onUnifiedLayerArrange,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  scrollRef: MutableRefObject<HTMLDivElement | null>
  getCanvasOrigin: () => { originX: number; originY: number }
  scale: number
  clipboardHistoryRef: MutableRefObject<ClipboardEntry[]>
  canUseClipboardEntry: (entry: { sourceObjectIds: string[] }) => boolean
  onShapeSelectionHistory: CenterCanvasConfig['onShapeSelectionHistory']
  onUnifiedLayerArrange: CenterCanvasConfig['onUnifiedLayerArrange']
}) {
  const createDuplicateId = useCallback((prefix: 'shape' | 'text') => {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  }, [])

  /** Returns the center of the visible canvas viewport in canvas doc-px coordinates. */
  const getViewportCenterCanvasPoint = useCallback((): { x: number; y: number } | null => {
    const scroller = scrollRef.current

    if (!scroller) return null

    const { originX, originY } = getCanvasOrigin()

    return {
      x: (scroller.scrollLeft + scroller.clientWidth / 2 - originX) / scale,
      y: (scroller.scrollTop + scroller.clientHeight / 2 - originY) / scale,
    }
  }, [getCanvasOrigin, scale, scrollRef])

  const pasteFromClipboard = useCallback(
    (
      inPlace: boolean,
      canvasPoint?: { canvasX: number; canvasY: number } | null,
      /** Duplicate (Ctrl/Cmd+D) reuses this same paste pipeline with `inPlace: true` plus a
       *  small visible offset, instead of landing exactly on top of the source like a real
       *  "Paste in Place" does. */
      duplicateOffsetPt?: number
    ): boolean => {
      const mgr = internalShapeManagerRef.current

      if (!mgr) return false

      const entry = clipboardHistoryRef.current.find((candidate) => canUseClipboardEntry(candidate))

      if (!entry) {
        if (clipboardHistoryRef.current.length > 0) {
          toast.error(LOCKED_PASTE_MESSAGE)
        }

        return false
      }

      // Pre-compute in-place base positions for all elements.
      const shapeBases = entry.shapeSnapshots.map((snap) => resolveShapePastePlacement(snap, 0))
      const textBases = entry.textSnapshots.map((snap) => ({ x: snap.x, y: snap.y }))

      // For viewport-centered paste compute a single translation that shifts the group's
      // bounding-box center to the visible viewport center. inPlace and right-click-point
      // pastes skip this and use tx/ty = 0.
      let tx = 0
      let ty = 0

      if (duplicateOffsetPt != null) {
        tx = duplicateOffsetPt
        ty = duplicateOffsetPt
      } else if (!inPlace && !canvasPoint) {
        const viewportCenter = getViewportCenterCanvasPoint()

        if (viewportCenter) {
          const offset = computeViewportCenteredPasteOffset({
            shapeSnapshots: entry.shapeSnapshots,
            textSnapshots: entry.textSnapshots,
            shapeBases,
            textBases,
            viewportCenter,
          })

          if (offset) {
            tx = offset.dx
            ty = offset.dy
          }
        }
      }

      const pastedShapeIds: string[] = []

      // Ctrl+V is one user action — every shape/text `add` and the trailing z-order fix-up
      // below share this one historyTxnId so `undo()` (use-undo-redo.ts) groups them into a
      // single compound step. A group member additionally sets `isGroupCreation` (rather than
      // using a `group:`-prefixed id) so persistence still knows to POST /elements/group for it,
      // without that prefix forcing `applyHistoryPersistencePlan`'s structural-txn branch (which
      // assumes a *pure* group/ungroup transaction) onto the rest of this mixed paste.
      const pasteTxnId = `paste:${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

      // Assign every pasted id up front (before anything is created) so parentGroupId can be
      // remapped to the *new* ids on the very first write, and so groups can be created only
      // once every descendant they need already exists.
      const sourceIdToPastedId = new Map<string, string>()

      for (const snapshot of entry.shapeSnapshots) {
        sourceIdToPastedId.set(snapshot.id, createDuplicateId('shape'))
      }

      for (const snapshot of entry.textSnapshots) {
        sourceIdToPastedId.set(snapshot.id, createDuplicateId('text'))
      }

      // Text boxes are always leaves — add them before any shape so a pasted group's nested
      // text already exists once the group triggers its own persistence below (home.tsx's
      // handleShapeCreated reads shapeManagerRef.current.getShapes() at that moment to build
      // the POST /group payload). Routed through ShapeManager (not TextManager) — see
      // frontend-recipes for the text-unification effort; addShapeData fires onShapeCreated,
      // which is home.tsx's persistence/undo entry point for shapes.
      for (const [i, snapshot] of entry.textSnapshots.entries()) {
        const clone: ShapeData = {
          ...snapshot,
          id: sourceIdToPastedId.get(snapshot.id)!,
          x: canvasPoint ? canvasPoint.canvasX : textBases[i].x + tx,
          y: canvasPoint ? canvasPoint.canvasY : textBases[i].y + ty,
          locked: false,
          frozen: false,
          // A pasted/duplicated frame is a new, independent object — it must not inherit the
          // original's text-linking chain identity (WMPCM-6852 uid-collision corruption).
          uid: undefined,
          parentGroupId: remapClipboardParentGroupId(snapshot.parentGroupId, sourceIdToPastedId),
        }

        mgr.addShapeData(clone, { selectAfterCreate: false, meta: { historyTxnId: pasteTxnId } })
        pastedShapeIds.push(clone.id)
      }

      // A group must be created after every descendant it needs (shapes below, text already
      // added above) — sort deepest-nested first so an outer group is never created before an
      // inner one it contains.
      const shapeSnapshotById = new Map(entry.shapeSnapshots.map((s) => [s.id, s]))
      const orderedShapeSnapshots = entry.shapeSnapshots
        .map((snapshot, i) => ({ snapshot, i }))
        .sort(
          (a, b) =>
            shapeSnapshotNestingDepth(b.snapshot, shapeSnapshotById) -
            shapeSnapshotNestingDepth(a.snapshot, shapeSnapshotById)
        )

      // Build each pasted shape as one fully-formed ShapeData object (kindId, photoFill, group
      // fields, everything) rather than creating a generic shell and patching it afterward —
      // home.tsx's handleShapeCreated persists synchronously off the shape it's handed, so a
      // shell created without its real kindId/type-specific fields would be persisted as a
      // basic rectangle (kindId 1) before any follow-up correction, silently reverting to that
      // on the next reload (SC-G-08 regression).
      for (const { snapshot, i } of orderedShapeSnapshots) {
        const { pastePanelAnchor: _pastePanelAnchor, ...shapeFields } = snapshot
        const { x, y } = canvasPoint
          ? { x: canvasPoint.canvasX, y: canvasPoint.canvasY }
          : { x: shapeBases[i].x + tx, y: shapeBases[i].y + ty }
        const fullShape: ShapeData = {
          ...shapeFields,
          id: sourceIdToPastedId.get(snapshot.id)!,
          x,
          y,
          rotation: snapshot.rotation ?? 0,
          locked: false,
          frozen: false,
          // Same rule as the text clone above, and for the same reason: since WMPCM-7988 a shape
          // carrying `shapeTextParagraphs` is a chain member exactly like a Text-tool frame, and
          // chains reference members by `uid`, not `id`. Letting it through would give two objects
          // one uid — the chain's uid→frame map is last-writer-wins, so the chain could bind to
          // this copy instead of the original (WMPCM-6852 uid-collision corruption, WMPCM-7012).
          // `shapeTextParagraphs` deliberately stays: the copy keeps its visible text, just not
          // the link.
          uid: undefined,
          parentGroupId: remapClipboardParentGroupId(snapshot.parentGroupId, sourceIdToPastedId),
        }

        const created = mgr.addShapeData(fullShape, {
          selectAfterCreate: false,
          meta: {
            historyTxnId: pasteTxnId,
            ...(isGroupShape(fullShape) ? { isGroupCreation: true } : {}),
          },
        })

        pastedShapeIds.push(created.id)
      }

      // Each shape/text add above appended its id to unifiedLayerIds in *creation* order (see
      // handleShapeCreated/handleTextBoxCommitted in home.tsx) — which is deepest-nested-first
      // for persistence reasons above, not the original relative stacking order. Restore that
      // order now, remapped to the pasted ids (bringToFront would only preserve creation order,
      // not fix it — insertOrdered applies this exact sequence instead).
      if (entry.sourceZOrder.length > 0) {
        const pastedZOrder = entry.sourceZOrder
          .map((sourceId) => sourceIdToPastedId.get(sourceId))
          .filter((id): id is string => id != null)

        if (pastedZOrder.length > 0) {
          onUnifiedLayerArrange?.(pastedZOrder, 'insertOrdered', { historyTxnId: pasteTxnId })
        }
      }

      const beforeShapeIds = mgr.getSelectedShapeIds()

      if (pastedShapeIds.length > 0) {
        mgr.selectShapes(pastedShapeIds)
      } else {
        mgr.clearSelection()
      }

      onShapeSelectionHistory?.(beforeShapeIds, mgr.getSelectedShapeIds(), {
        beforeTextIds: [],
        afterTextIds: [],
        source: 'command',
        historyTxnId: pasteTxnId,
      })

      return pastedShapeIds.length > 0
    },
    [
      canUseClipboardEntry,
      createDuplicateId,
      getViewportCenterCanvasPoint,
      onShapeSelectionHistory,
      onUnifiedLayerArrange,
      internalShapeManagerRef,
      clipboardHistoryRef,
    ]
  )

  return { pasteFromClipboard }
}
